import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  concatMap,
  debounceTime,
  filter,
  first,
  from,
  map,
  pipe,
  switchMap,
  takeWhile,
  tap,
  toArray,
} from 'rxjs';

import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  GetChatMessagesGQL,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
} from 'ZalamoLibrary/src/lib/ui/chat/graphql';

import { ScrollbarComponent } from 'ZalamoLibrary/src/lib/ui/scrollbar/scrollbar.component';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';

import {
  MarkSupportThreadAsReadGQL,
  NewMessageFromSupportDocument,
  NewMessageFromSupportSubscription,
  NewMessageFromSupportSubscriptionVariables,
  StandaloneSupportMessage,
  SupportStatus,
} from '../../support/graphql';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { QueryRef } from 'apollo-angular';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UploadService } from 'ZalamoLibrary/src/lib/upload/upload.service';
import { UploadContext } from 'Upload/upload/graphql';
import { SupportMessageType } from '../../../../../../zalamo-support/src/graphql';
import { MessageContentType } from 'ZalamoLibrary/src/lib/ui/chat/types';
import { isUnsupportedJpegColorSpaceError } from 'ZalamoLibrary/src/lib/core/image/image-metadata.service';

interface FileToUpload {
  name: string;
  type: string;
  url: SafeResourceUrl;
  file: File;
  extension: string;
  uploadCompleted?: boolean;
  uploadInProgress?: boolean;
}

@Component({
  selector: 'lib-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatWidgetComponent
  extends AliveState
  implements OnInit, AfterViewChecked
{
  @Output() hideMe = new EventEmitter();
  @Output() closeMe = new EventEmitter();
  @Input() chatTitle = '';
  @Input() chatAvatar = '';
  @Input() onlineStatus: SupportStatus = SupportStatus.Online;
  @Input() unreadCount = 0;
  conversationId$ = new ReplaySubject<string>(1);
  supportThreadId$ = new ReplaySubject<string>(1);
  @ViewChild('inputNode', { static: true }) inputNode: ElementRef<HTMLElement>;
  totalMessagesDownloaded = 0;
  firstLoad = true;
  @ViewChild(ScrollbarComponent, { static: true })
  scrollBar: ScrollbarComponent;
  fullConversationLoaded = false;
  messagesQuery: QueryRef<GetChatMessagesQuery, GetChatMessagesQueryVariables>;
  loadMessagesStream$ = combineLatest([
    this.organizationService.getCurrentOrganization(),
    this.conversationId$.pipe(RxjsOperators.filterTruthy()),
  ]).pipe(
    first(),
    switchMap(([organizationId, conversationId]) => {
      this.messagesQuery = this.getChatMessagesGQL.watch({
        variables: {
          organizationId,
          conversationId,
        },
      });

      return this.messagesQuery.valueChanges.pipe(
        throwOnQueryError,
        filter(hasCompleteQueryData),
        map((result) => result.data['getSupportChatMessages']),
      );
    }),
  );
  filesToBeUploaded$ = new BehaviorSubject<Array<FileToUpload>>([]);
  uploadInProgress$ = this.filesToBeUploaded$.pipe(
    map(
      (files) =>
        files.filter(
          ({ uploadInProgress, uploadCompleted }) =>
            uploadInProgress || uploadCompleted,
        ).length,
    ),
  );
  uploadProgress$ = this.filesToBeUploaded$.pipe(
    map(
      (files) =>
        (files.filter(({ uploadCompleted }) => uploadCompleted).length * 100) /
        files.length,
    ),
  );
  lastOffset = 0;
  lastResponseLength = 0;
  @Output() addMessage = new EventEmitter<{
    message: any;
    type: SupportMessageType;
  }>();
  scrollTopFixRequired = false;
  messages$ = this.loadMessagesStream$.pipe(
    map((messages) => [...messages].sort((a, b) => a.date - b.date)),
    tap((messages) => (this.totalMessagesDownloaded = messages.length)),
    tap((messages) => {
      if (this.firstLoad) {
        this.firstLoad = false;
        this.scrollBar.scrollToBottom();
        this.lastResponseLength = this.totalMessagesDownloaded;
      } else {
        let isScrolled = false;

        const { nativeElement } = this.scrollBar.container;
        const { scrollHeight, scrollTop, clientHeight } = nativeElement;
        console.log(scrollTop, scrollHeight);
        isScrolled = scrollHeight !== scrollTop + clientHeight;
        this.cd.detectChanges();
        if (!isScrolled) {
          this.scrollBar.scrollToBottom();
        }
      }
    }),
    tap(() => {
      if (this.unreadCount) {
        this.unreadCount = 0;
        console.log('MARK AS READ');
        this.organizationService
          .getCurrentOrganization()
          .pipe(
            first(),
            switchMap((organizationId) =>
              this.markSupportThreadAsReadGQL.mutate({
                variables: {
                  organizationId,
                },
              }),
            ),
          )
          .subscribe();
      }
    }),
  );
  getNewMessages$ = new Subject();
  currentScrollPosition: { percent: number; currentScrollTopHeight: number } =
    null;
  restartScroll = new Subject();
  lastV = 0;
  loadInProgress = false;
  lastMessagesInHTMLCount = 0;
  lastContainerHeight = 0;
  lastContainerScrollTop = 0;
  dragOver$ = new BehaviorSubject(false);

  @Input() set conversationId(conversationId: string) {
    this.conversationId$.next(conversationId);
  }

  @Input() set supportThreadId(conversationId: string) {
    this.supportThreadId$.next(conversationId);
  }

  constructor(
    private getChatMessagesGQL: GetChatMessagesGQL,
    private organizationService: OrganizationService,
    private cd: ChangeDetectorRef,
    private markSupportThreadAsReadGQL: MarkSupportThreadAsReadGQL,
    private domSanitizer: DomSanitizer,
    private uploadService: UploadService,
  ) {
    super();
  }

  ngAfterViewChecked() {
    if (this.scrollTopFixRequired) {
      this.scrollBar.container.nativeElement
        .querySelectorAll('.message')
        [this.lastResponseLength].scrollIntoView();
      this.scrollTopFixRequired = false;
    }
  }

  currentScroll(v: { percent: number; currentScrollTopHeight: number }) {
    if (
      !this.loadInProgress &&
      this.totalMessagesDownloaded > 0 &&
      v.percent * this.totalMessagesDownloaded < 10 &&
      !this.fullConversationLoaded
    ) {
      this.currentScrollPosition = v;

      this.getNewMessages$.next(v.percent * this.totalMessagesDownloaded);
    }
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      combineLatest([this.supportThreadId$, this.loadMessagesStream$]).pipe(
        first(),
        tap((x) => console.log('GET NEW MESSAGES SUB', x)),
        tap(([supportThreadId]) =>
          this.messagesQuery.subscribeToMore<
            NewMessageFromSupportSubscription,
            NewMessageFromSupportSubscriptionVariables
          >({
            document: NewMessageFromSupportDocument,
            variables: {
              supportThreadId,
            },
            updateQuery: (
              _previousQueryResult,
              { subscriptionData, complete, previousData },
            ) => {
              if (!complete) {
                return undefined;
              }
              const previousQueryResult = previousData;
              if (!subscriptionData.data) {
                return previousQueryResult;
              }
              const newFeedItem = subscriptionData.data.messageFromSupportSent;
              return {
                ...previousQueryResult,
                getSupportChatMessages: [
                  ...previousQueryResult.getSupportChatMessages,

                  newFeedItem,
                ],
              };
            },
          }),
        ),
      ),
      this.getNewMessages$.pipe(
        filter((v) => !this.loadInProgress),
        tap(() => (this.loadInProgress = true)),
        debounceTime(50),
        takeWhile(() => !this.fullConversationLoaded),
        switchMap(() => this.organizationService.getCurrentOrganization()),
        tap((organizationId) => {
          console.log('ASK FOR OLDER COMMENTS');

          this.lastOffset = this.lastOffset + 50;
          this.messagesQuery
            .fetchMore({
              variables: {
                organizationId,
                offset: this.lastOffset,
                limit: 50,
              },

              updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                this.loadInProgress = false;
                if (!fetchMoreResult) {
                  return previousQueryResult;
                }
                this.lastResponseLength =
                  fetchMoreResult.getSupportChatMessages.length;
                this.scrollTopFixRequired =
                  this.currentScrollPosition.percent === 0;
                if (this.lastResponseLength < 50) {
                  this.fullConversationLoaded = true;
                }

                return Object.assign({}, previousQueryResult, {
                  getSupportChatMessages: [
                    ...previousQueryResult.getSupportChatMessages,
                    ...fetchMoreResult.getSupportChatMessages,
                  ],
                });
              },
            })
            .then(() => {
              // this.restartScroll.next(this.currentScrollPosition);
              {
                const { nativeElement } = this.scrollBar.container;
                const { scrollHeight, scrollTop } = nativeElement;
                this.lastMessagesInHTMLCount = nativeElement.childNodes.length;
                this.lastContainerHeight = scrollHeight;
                this.lastContainerScrollTop = scrollTop;
              }
              this.cd.detectChanges();
              const { nativeElement } = this.scrollBar.container;
              const { scrollHeight, scrollTop } = nativeElement;
              const childElementCount = nativeElement.childNodes.length;

              nativeElement.scrollTop +=
                nativeElement.scrollHeight - this.lastContainerHeight;

              this.loadInProgress = false;
            });
        }),
      ),
    );
  }

  publishMessage<K extends SupportMessageType>(
    type: SupportMessageType,
    message: MessageContentType[K],
  ) {
    this.addMessage.next({ message, type });
  }

  async sendMessage(event: InputEvent) {
    event.preventDefault();
    const message = this.inputNode.nativeElement.innerHTML;
    if (this.filesToBeUploaded$.value.length) {
      const files = this.filesToBeUploaded$.value.map(({ file }) => file);
      if (!(await this.uploadService.validateSupportedJpegs(files))) {
        return;
      }

      from(this.filesToBeUploaded$.value)
        .pipe(
          concatMap(({ file, extension }) => {
            const current = [...this.filesToBeUploaded$.value];
            const currentFile = current.find((entry) => entry.file === file);
            currentFile.uploadInProgress = true;
            this.filesToBeUploaded$.next(current);

            return this.uploadService
              .uploadSingleFile(file, UploadContext.ChatAttachment, 'zalamo')
              .pipe(
                map((fileName) => ({ file, fileName, extension })),
                tap(() => {
                  currentFile.uploadCompleted = true;
                  currentFile.uploadInProgress = false;
                  this.filesToBeUploaded$.next(current);
                }),
              );
          }),
          toArray(),
        )

        .subscribe({
          next: (res) => {
            console.log(res);
            this.filesToBeUploaded$.next([]);
            this.publishMessage(
              SupportMessageType.Attachments,
              res.map(({ file, fileName, extension }) => ({
                name: file.name,
                type: file.type,
                blob: fileName,
                extension,
              })),
            );
          },
          error: (error) => {
            this.filesToBeUploaded$.next(
              this.filesToBeUploaded$.value.map((entry) => ({
                ...entry,
                uploadInProgress: false,
              })),
            );
            if (!isUnsupportedJpegColorSpaceError(error)) {
              console.error(error);
            }
          },
        });
    }
    if (message) {
      this.publishMessage(SupportMessageType.Text, message);
    }
    this.inputNode.nativeElement.innerHTML = '';
  }

  trackMessage(i, { standaloneSupportMessageId }: StandaloneSupportMessage) {
    return standaloneSupportMessageId;
  }

  inputEvent(event: InputEvent) {
    console.log(event.inputType);
    console.log(event);
  }

  inputFileChanged(input: HTMLInputElement) {
    const files: Array<FileToUpload> = [];
    Array.from(input.files).forEach((file) => {
      files.push({
        name: file.name,
        type: file.type,
        url: this.domSanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(file),
        ),
        file,
        extension: /(?:\.([^.]+))?$/.exec(file.name)[1],
      });
    });
    this.filesToBeUploaded$.next([
      ...this.filesToBeUploaded$.value,
      ...this.filterFiles(files),
    ]);
  }

  filterFiles(newFiles) {
    return newFiles.filter(
      ({ file }) =>
        !this.filesToBeUploaded$.value
          .map(({ file }) => `${file.name}${file.size}`)
          .includes(`${file.name}${file.size}`),
    );
  }

  onPaste(event: ClipboardEvent) {
    console.log(Array.from(event.clipboardData.items));

    if (event.clipboardData.items[0].kind === 'file') {
      event.preventDefault();
      event.stopPropagation();

      const files: Array<FileToUpload> = [];
      Array.from(event.clipboardData.items).forEach((value) => {
        const file = value.getAsFile();

        files.push({
          name: file.name,
          type: file.type,
          url: this.domSanitizer.bypassSecurityTrustResourceUrl(
            URL.createObjectURL(file),
          ),
          file,
          extension: /(?:\.([^.]+))?$/.exec(file.name)[1],
        });
      });

      this.filesToBeUploaded$.next([
        ...this.filesToBeUploaded$.value,
        ...this.filterFiles(files),
      ]);
    }
  }

  dropped(event: DragEvent) {
    event.preventDefault();
    const files: Array<FileToUpload> = [];
    console.log(event.dataTransfer.files);
    console.log(Array.from(event.dataTransfer.files));
    Array.from(event.dataTransfer.files).forEach((file) => {
      files.push({
        name: file.name,
        type: file.type,
        url: this.domSanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(file),
        ),
        file,
        extension: /(?:\.([^.]+))?$/.exec(file.name)[1],
      });
    });
    this.filesToBeUploaded$.next([
      ...this.filesToBeUploaded$.value,
      ...this.filterFiles(files),
    ]);
  }

  deleteFileToBeUploaded(fileToDelete: File) {
    this.filesToBeUploaded$.next(
      this.filesToBeUploaded$.value.filter(({ file }) => file !== fileToDelete),
    );
  }
}
